import Editor from "@monaco-editor/react"
import AddIcon from "@mui/icons-material/Add"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import DownloadIcon from "@mui/icons-material/Download"
import EditIcon from "@mui/icons-material/Edit"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import StopIcon from "@mui/icons-material/Stop"
import UploadIcon from "@mui/icons-material/Upload"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { useMemo, useRef, useState } from "react"
import {
  extractConditionNames,
  extractVariableNames,
  makeId,
  type PromptDraft
} from "./runner"
import {
  buildSuiteTemplate,
  downloadJson,
  parseSuiteJson,
  SUITE_SPEC_MARKDOWN
} from "./template"
import type {
  PlaygroundSettings,
  PromptTestCase,
  PromptTestSuite,
  RunProgress
} from "./types"

interface CasesTabProps {
  draft: PromptDraft
  suite: PromptTestSuite
  onSuiteChange: (suite: PromptTestSuite) => void
  settings: PlaygroundSettings
  onSettingsChange: (patch: Partial<PlaygroundSettings>) => void
  running: boolean
  progress: RunProgress | null
  onRunCases: (caseIds?: string[]) => void
  onStop: () => void
}

function expectationSummary(testCase: PromptTestCase): string[] {
  const expect = testCase.expect
  if (!expect) return []
  const labels: string[] = []
  if (expect.contains?.length)
    labels.push(`contains ×${expect.contains.length}`)
  if (expect.notContains?.length)
    labels.push(`notContains ×${expect.notContains.length}`)
  if (expect.regex) labels.push("regex")
  if (expect.json?.length) labels.push(`json ×${expect.json.length}`)
  if (expect.maxLatencyMs) labels.push(`≤${expect.maxLatencyMs}ms`)
  if (expect.judge) labels.push("judge")
  return labels
}

export default function CasesTab(props: CasesTabProps) {
  const {
    draft,
    suite,
    onSuiteChange,
    settings,
    onSettingsChange,
    running,
    progress,
    onRunCases,
    onStop
  } = props

  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState("")
  const [importErrors, setImportErrors] = useState<string[]>([])
  const [editing, setEditing] = useState<PromptTestCase | null>(null)
  const [editorJson, setEditorJson] = useState("")
  const [editorError, setEditorError] = useState("")
  const [selected, setSelected] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const variableNames = useMemo(
    () => extractVariableNames(draft.systemPrompt),
    [draft.systemPrompt]
  )

  const conditionNames = useMemo(
    () => extractConditionNames(draft.systemPrompt),
    [draft.systemPrompt]
  )

  function patchCases(cases: PromptTestCase[]) {
    onSuiteChange({ ...suite, cases })
  }

  function handleImport(text: string) {
    const parsed = parseSuiteJson(text, draft.name)
    if (!parsed.suite) {
      setImportErrors(parsed.errors)
      return
    }
    onSuiteChange(parsed.suite)
    setImportErrors([])
    setImportOpen(false)
    setImportText("")
    showSuccessToast(
      `${parsed.suite.cases.length} hazır soru yüklendi${
        parsed.warnings.length ? ` (${parsed.warnings.join(" ")})` : ""
      }`
    )
  }

  function openEditor(testCase: PromptTestCase | null) {
    const base: PromptTestCase = testCase ?? {
      id: makeId("case"),
      name: "Yeni soru",
      query: ""
    }
    setEditing(base)
    setEditorJson(JSON.stringify(base, null, 2))
    setEditorError("")
  }

  function saveEditor() {
    try {
      const parsed = JSON.parse(editorJson) as PromptTestCase
      if (!parsed.query?.trim()) {
        setEditorError("`query` boş olamaz")
        return
      }
      parsed.id = parsed.id || makeId("case")
      const exists = suite.cases.some((c) => c.id === parsed.id)
      patchCases(
        exists
          ? suite.cases.map((c) => (c.id === parsed.id ? parsed : c))
          : [...suite.cases, parsed]
      )
      setEditing(null)
    } catch (error) {
      setEditorError(String(error))
    }
  }

  const allSelected =
    selected.length > 0 && selected.length === suite.cases.length

  return (
    <Stack spacing={1.25} sx={{ height: "100%", minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => openEditor(null)}
        >
          Soru ekle
        </Button>
        <Button
          size="small"
          startIcon={<UploadIcon />}
          onClick={() => setImportOpen(true)}
        >
          İçe aktar
        </Button>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          disabled={!suite.cases.length}
          onClick={() =>
            downloadJson(`${draft.name || "prompt"}-suite.json`, suite)
          }
        >
          Dışa aktar
        </Button>
        <Tooltip title="Boş şablonu indir — Claude'a verip hazır soru ürettirebilirsin">
          <Button
            size="small"
            onClick={() =>
              downloadJson(
                `${draft.name || "prompt"}-suite-template.json`,
                buildSuiteTemplate(draft.name, variableNames, conditionNames)
              )
            }
          >
            Şablon
          </Button>
        </Tooltip>
        <Tooltip title="JSON format dokümanını panoya kopyala">
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard
                .writeText(
                  `${SUITE_SPEC_MARKDOWN}\n\n## Prompt: ${draft.name}\n## Placeholder'lar: ${
                    variableNames.map((v) => `{{${v}}}`).join(", ") || "yok"
                  }\n## Koşullu bloklar: ${
                    conditionNames.join(", ") || "yok"
                  }\n\n## Prompt metni\n\`\`\`\n${draft.systemPrompt}\n\`\`\``
                )
                .then(() =>
                  showSuccessToast("Format + prompt panoya kopyalandı")
                )
                .catch(() => showErrorToast("Kopyalanamadı"))
            }}
          >
            Format kopyala
          </Button>
        </Tooltip>

        <Box flex={1} />

        <TextField
          size="small"
          type="number"
          label="Tekrar"
          sx={{ width: 92 }}
          value={settings.repeats}
          onChange={(event) =>
            onSettingsChange({
              repeats: Math.max(
                1,
                Math.min(10, Number(event.target.value) || 1)
              )
            })
          }
        />
        <TextField
          size="small"
          type="number"
          label="Eşzamanlı"
          sx={{ width: 106 }}
          value={settings.concurrency}
          onChange={(event) =>
            onSettingsChange({
              concurrency: Math.max(
                1,
                Math.min(8, Number(event.target.value) || 1)
              )
            })
          }
        />
        {running ? (
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<StopIcon />}
            onClick={onStop}
          >
            Durdur
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!suite.cases.length}
            onClick={() => onRunCases(selected.length ? selected : undefined)}
          >
            {selected.length
              ? `Seçili ${selected.length} testi çalıştır`
              : "Tümünü çalıştır"}
          </Button>
        )}
      </Stack>

      {running && progress && (
        <Box>
          <LinearProgress
            variant="determinate"
            value={progress.total ? (progress.done / progress.total) * 100 : 0}
          />
          <Typography variant="caption" color="text.secondary">
            {progress.done}/{progress.total} — {progress.currentCase}
          </Typography>
        </Box>
      )}

      {!suite.cases.length && (
        <Alert severity="info" variant="outlined">
          Henüz hazır soru yok. “Format kopyala” ile şablonu Claude’a verip soru
          seti ürettir, sonra “İçe aktar” ile yükle.
        </Alert>
      )}

      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={(event) =>
                    setSelected(
                      event.target.checked ? suite.cases.map((c) => c.id) : []
                    )
                  }
                />
              </TableCell>
              <TableCell>Ad</TableCell>
              <TableCell>Soru</TableCell>
              <TableCell>Beklenti</TableCell>
              <TableCell align="right" width={120}>
                İşlem
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suite.cases.map((testCase) => (
              <TableRow
                key={testCase.id}
                hover
                sx={{ opacity: testCase.skip ? 0.5 : 1 }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selected.includes(testCase.id)}
                    onChange={(event) =>
                      setSelected((prev) =>
                        event.target.checked
                          ? [...prev, testCase.id]
                          : prev.filter((id) => id !== testCase.id)
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {testCase.name}
                    </Typography>
                    {testCase.tags?.map((tag) => (
                      <Chip
                        key={tag}
                        size="small"
                        variant="outlined"
                        label={tag}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    display="block"
                  >
                    {testCase.query}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {expectationSummary(testCase).map((label) => (
                      <Chip key={label} size="small" label={label} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Sadece bunu çalıştır">
                    <span>
                      <IconButton
                        size="small"
                        disabled={running}
                        onClick={() => onRunCases([testCase.id])}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton size="small" onClick={() => openEditor(testCase)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      patchCases(
                        suite.cases.filter((c) => c.id !== testCase.id)
                      )
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Hazır soruları içe aktar</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} mt={0.5}>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              hidden
              onChange={async (event) => {
                const file = event.target.files?.[0]
                if (!file) return
                setImportText(await file.text())
                event.target.value = ""
              }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={() => fileInputRef.current?.click()}
              >
                Dosya seç
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                alignSelf="center"
              >
                ya da JSON’u aşağıya yapıştır
              </Typography>
            </Stack>
            <TextField
              multiline
              minRows={12}
              value={importText}
              onChange={(event) => setImportText(event.target.value)}
              placeholder='{ "version": 1, "cases": [ ... ] }'
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            />
            {importErrors.length > 0 && (
              <Alert severity="error">
                {importErrors.map((error) => (
                  <div key={error}>{error}</div>
                ))}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={!importText.trim()}
            onClick={() => handleImport(importText)}
          >
            Yükle
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Hazır soru</DialogTitle>
        <DialogContent>
          <Stack spacing={1} mt={0.5}>
            <Editor
              height="420px"
              defaultLanguage="json"
              value={editorJson}
              onChange={(value) => setEditorJson(value ?? "")}
              options={{
                minimap: { enabled: false },
                fontSize: 12,
                wordWrap: "on",
                scrollBeyondLastLine: false
              }}
            />
            {editorError && <Alert severity="error">{editorError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Vazgeç</Button>
          <Button variant="contained" onClick={saveEditor}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
